import React, { useState, useEffect } from 'react';
import './AmazonSales.css';
import API_BASE_URL from '../config';

const AmazonSales = () => {
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [itemStatus, setItemStatus] = useState('');
  const [fulfillmentChannel, setFulfillmentChannel] = useState('');
  const [salesChannel, setSalesChannel] = useState('');

  // Get unique filter options
  const [filterOptions, setFilterOptions] = useState({
    itemStatuses: [],
    fulfillmentChannels: [],
    salesChannels: []
  });

  useEffect(() => {
    // Set default date range to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    // Load filter options
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/amazon-sales/filter-options`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFilterOptions({
            itemStatuses: data.item_statuses,
            fulfillmentChannels: data.fulfillment_channels,
            salesChannels: data.sales_channels
          });
        } else {
          throw new Error(data.error || 'Failed to fetch filter options');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch filter options');
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
      setError('Failed to load filter options: ' + err.message);
    }
  };

  const fetchSalesData = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/amazon-sales/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          item_status: itemStatus || null,
          fulfillment_channel: fulfillmentChannel || null,
          sales_channel: salesChannel || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSalesData(data);
        } else {
          throw new Error(data.error || 'Failed to fetch sales data');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch sales data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching sales data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const handleFilterChange = () => {
    // Reset data when filters change
    setSalesData(null);
  };

  return (
    <div className="amazon-sales">
      <div className="amazon-header">
        <h1>Amazon Sales</h1>
        <p>Sales data from Amazon marketplace</p>
      </div>

      <div className="amazon-content">
        <div className="controls-section">
          <div className="filters-container">
            <div className="filter-group">
              <label htmlFor="startDate">Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  handleFilterChange();
                }}
                disabled={loading}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="endDate">End Date:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  handleFilterChange();
                }}
                disabled={loading}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="itemStatus">Item Status:</label>
              <select
                id="itemStatus"
                value={itemStatus}
                onChange={(e) => {
                  setItemStatus(e.target.value);
                  handleFilterChange();
                }}
                disabled={loading}
              >
                <option value="">All Statuses</option>
                {filterOptions.itemStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="fulfillmentChannel">Fulfillment Channel:</label>
              <select
                id="fulfillmentChannel"
                value={fulfillmentChannel}
                onChange={(e) => {
                  setFulfillmentChannel(e.target.value);
                  handleFilterChange();
                }}
                disabled={loading}
              >
                <option value="">All Channels</option>
                {filterOptions.fulfillmentChannels.map(channel => (
                  <option key={channel} value={channel}>{channel}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="salesChannel">Sales Channel:</label>
              <select
                id="salesChannel"
                value={salesChannel}
                onChange={(e) => {
                  setSalesChannel(e.target.value);
                  handleFilterChange();
                }}
                disabled={loading}
              >
                <option value="">All Channels</option>
                {filterOptions.salesChannels.map(channel => (
                  <option key={channel} value={channel}>{channel}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="action-buttons">
            <button
              className="btn btn-primary"
              onClick={fetchSalesData}
              disabled={loading || !startDate || !endDate}
            >
              {loading ? 'Loading...' : 'Get Sales Data'}
            </button>
          </div>
          
          {loading && (
            <div className="loading-indicator">
              <div className="loading-spinner"></div>
              <span>Loading sales data...</span>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        {salesData && (
          <div className="sales-data-section">
            <h2>Sales Data Results</h2>
            
            <div className="sales-summary">
              <div className="summary-card">
                <h3>Total ASINs</h3>
                <div className="summary-value">{salesData.summary.record_count}</div>
              </div>
              <div className="summary-card">
                <h3>Total Quantity</h3>
                <div className="summary-value">{formatNumber(salesData.summary.total_quantity)}</div>
              </div>
              <div className="summary-card">
                <h3>Total Revenue</h3>
                <div className="summary-value">{formatCurrency(salesData.summary.total_revenue)}</div>
              </div>
              <div className="summary-card">
                <h3>Total Orders</h3>
                <div className="summary-value">{formatNumber(salesData.summary.total_orders)}</div>
              </div>
            </div>

            <div className="sales-table-container">
              <h3>Sales by ASIN</h3>
              <div className="table-container">
                <table className="sales-table">
                  <thead>
                    <tr>
                      <th>ASIN</th>
                      <th>IGD Name</th>
                      <th>Quantity</th>
                      <th>Revenue</th>
                      <th>Orders</th>
                      <th>Average Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.data.map((item, index) => (
                      <tr key={index}>
                        <td>{item.asin}</td>
                        <td>{item.igd_name}</td>
                        <td>{formatNumber(item.total_quantity)}</td>
                        <td>{formatCurrency(item.total_item_price)}</td>
                        <td>{formatNumber(item.order_count)}</td>
                        <td>
                          {item.total_quantity > 0 
                            ? formatCurrency(item.total_item_price / item.total_quantity)
                            : '$0.00'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmazonSales; 